import { Injectable } from '@angular/core'
import { IJobPostUserConfig } from '@employer/app/models/job-post-user-config.model'
import { EmployerUserService } from '@employer/app/services/employer-user.service'
import { E11NotificationMessage, E11NotificationsService } from '@engineering11/ui-lib/e11-notifications'
import { E11ErrorHandlerService } from '@engineering11/web-api-error'
import { ComponentStore } from '@ngrx/component-store'
import { tapResponse } from '@ngrx/operators'
import { from, Observable, switchMap } from 'rxjs'
import { of } from 'rxjs/internal/observable/of'
import { map } from 'rxjs/operators'

type UserHiringTeamSettingConfig = Omit<IJobPostUserConfig, 'id' | 'customerKey'>

export interface UserHiringTeamSettingState {
  config?: UserHiringTeamSettingConfig
  loaded: boolean
  // The user's document has not been created yet (the backend writes it shortly after signup).
  pending: boolean
}

const defaultState: UserHiringTeamSettingState = {
  loaded: false,
  pending: false,
  config: undefined,
}

@Injectable({
  providedIn: 'root',
})
export class UserHiringTeamConfigStore extends ComponentStore<UserHiringTeamSettingState> {
  constructor(
    private errorHandler: E11ErrorHandlerService,
    private notificationService: E11NotificationsService,
    private employerUserService: EmployerUserService
  ) {
    super(defaultState)
  }

  // Selectors
  readonly getState = this.select(s => s)
  readonly swimlaneConfig$ = this.select(s => s.config?.swimlanes)
  readonly configLoaded$ = this.select(s => s.loaded)
  readonly configPending$ = this.select(s => s.pending)

  // Effects
  readonly onFetchOrLoadHiringTeamConfig = this.effect((_$: Observable<void>) =>
    _$.pipe(
      switchMap(() => this.swimlaneConfig$),
      map(config => {
        if (config) {
          return of({ swimlanes: config })
        }
        return this.onLoadHiringTeamConfig()
      })
    )
  )

  readonly onLoadHiringTeamConfig = this.effect((_$: Observable<void>) =>
    _$.pipe(
      switchMap(() =>
        this.employerUserService.getJobPostConfigForCurrentCustomerUser().pipe(
          tapResponse(
            config => {
              if (config) this.setConfig({ swimlanes: config.swimlanes ?? [] })
              else this.setPending()
            },
            (err: Error) => this.errorHandler.handleError(err)
          )
        )
      )
    )
  )

  readonly onUpdateHiringTeamSwimlaneConfig = this.effect((payload$: Observable<UserHiringTeamSettingConfig>) =>
    payload$.pipe(
      switchMap(({ swimlanes }) =>
        from(this.employerUserService.updateJobPostConfig({ swimlanes })).pipe(
          tapResponse(
            _ => {
              this.setConfig({ swimlanes })
              this.notificationService.popNotificationMessage(successfulUpdateNotification)
            },
            (err: Error) => this.errorHandler.handleError(err)
          )
        )
      )
    )
  )

  // Reducers
  readonly setConfig = this.updater((state, config: UserHiringTeamSettingConfig) => ({
    ...state,
    config,
    loaded: true,
    pending: false,
  }))

  readonly setPending = this.updater(state => ({
    ...state,
    config: undefined,
    loaded: false,
    pending: true,
  }))
}

const successfulUpdateNotification: E11NotificationMessage = {
  title: 'Success',
  message: 'Your Decision Team settings have been saved.',
  type: 'success',
  dismissOnRouteChange: true,
  autoClose: true,
}
